import { useCallback, useEffect, useRef, useState } from "react";
import { getToken, forceRefreshToken } from "../services/cpln";
import { IS_DEPLOYMENT_ENV_PROD } from "../envVariables";
import { Deployment } from "../schema/types/workload/deployment";

interface UseDeploymentWebSocketOptions {
  org: string;
  gvc: string;
  workload: string;
  onMessage: (deployment: Deployment) => void;
  enabled?: boolean;
}

export type WebSocketConnectionStatus = "connecting" | "connected" | "disconnected" | "error";

const WS_URL = `wss://workload-status.${IS_DEPLOYMENT_ENV_PROD ? "" : "test."}cpln.io:443/register`;
const RECONNECT_DELAY_BASE = 1000;
const MAX_RECONNECT_DELAY = 30000;
const MAX_RECONNECT_ATTEMPTS = 10;
const CONNECTION_TIMEOUT = 25 * 60 * 1000; // 25 minutes

export function useDeploymentWebSocket({
  org,
  gvc,
  workload,
  onMessage,
  enabled = true,
}: UseDeploymentWebSocketOptions) {
  const [status, setStatus] = useState<WebSocketConnectionStatus>("disconnected");
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectAttemptRef = useRef(0);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const refreshTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onMessageRef = useRef(onMessage);

  // Keep onMessage ref up to date to avoid stale closures
  useEffect(() => {
    onMessageRef.current = onMessage;
  }, [onMessage]);

  const cleanup = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
      refreshTimeoutRef.current = null;
    }
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
  }, []);

  const connect = useCallback(async (options?: { forceTokenRefresh?: boolean }) => {
    if (!enabled) return;

    cleanup();
    setStatus("connecting");

    try {
      // Force refresh token on scheduled reconnections to ensure validity
      if (options?.forceTokenRefresh) {
        await forceRefreshToken();
      }
      const token = await getToken();
      const ws = new WebSocket(WS_URL);
      wsRef.current = ws;

      ws.onopen = () => {
        ws.send(
          JSON.stringify({
            token,
            interests: [{ org, gvc, workload }],
          }),
        );
        setStatus("connected");
        reconnectAttemptRef.current = 0;

        // Refresh connection before server timeout, force token refresh to ensure validity
        refreshTimeoutRef.current = setTimeout(() => {
          connect({ forceTokenRefresh: true });
        }, CONNECTION_TIMEOUT);
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          const deployment = data.eventType ? data.data.deployment : data;
          onMessageRef.current(deployment);
        } catch (e) {
          console.error("Failed to parse WebSocket message:", e);
        }
      };

      ws.onerror = () => {
        console.error("Deployment WebSocket error");
        setStatus("error");
      };

      ws.onclose = () => {
        setStatus("disconnected");
        wsRef.current = null;

        // Attempt reconnection with exponential backoff
        if (enabled && reconnectAttemptRef.current < MAX_RECONNECT_ATTEMPTS) {
          const delay = Math.min(RECONNECT_DELAY_BASE * Math.pow(2, reconnectAttemptRef.current), MAX_RECONNECT_DELAY);
          reconnectAttemptRef.current++;
          reconnectTimeoutRef.current = setTimeout(() => connect(), delay);
        }
      };
    } catch (e) {
      console.error("Failed to initialize WebSocket:", e);
      setStatus("error");
    }
  }, [enabled, org, gvc, workload, cleanup]);

  useEffect(() => {
    if (enabled) {
      connect();
    }
    return cleanup;
  }, [enabled, org, gvc, workload, connect, cleanup]);

  return { status, reconnect: connect };
}
